// Hosts the correct "add/edit a single record" side panel for each
// record-based change section (risk location, coverage, interested party,
// equipment, tool floater, fine art), and writes the saved record into the
// matching values.*_change.records array.
//
// This is a faithful port of AutomobilePolicy's V2 RecordPanelHost:
//   - Add   -> the section's standard RightPanel (blank form).
//   - Change (substitution) -> RecordReplacePanelV2 wrapper: pick the record
//     being replaced (top) + enter the replacement (the same form, below).
//     The pick is written into the form (type="Change", delete_* pointer, and
//     a delete_*_details snapshot), exactly like Automobile.
//   - Edit  -> the substitution wrapper is kept but its selector is locked
//     (disableSelect), and the form is pre-filled from the saved record.
//
// PERFORMANCE NOTE: the substitution pickers need the "existing records" lists
// (risk locations, coverages, etc.). Those lists are fetched by the
// useBusiness* hooks. To avoid firing all six list APIs on manage-view load,
// each section's substitution UI lives in its OWN component below and only
// that component calls its hook. Because a section component is mounted only
// when it is the active `target`, its list API fires only when that specific
// panel opens. The plain "Add"/"Edit" path renders the *RightPanel directly
// and needs none of these lists, so it triggers no extra list fetch.

import React from "react";
import produce from "immer";
import { IBusinessPolicyChange } from "../../../../../../../../redux/businessPolicy/businessPolicyChange/businessPolicyChane.types";
import { RiskLocationRightPanel } from "../../../RiskLocation/RiskLocationRightPanel";
import { CoverageRightPanel } from "../../../Coverage/CoverageRightPanel";
import { InterestedPartiesRightPanel } from "../../../InterestedParties/InterestedPartiesRightPanel";
import { EquipmentRightPanel } from "../../../Equipment/EquipmentRightPanel";
import { ToolFloaterRightPanel } from "../../../ToolFloater/ToolFloaterRightPanel";
import { FineArtRightPanel } from "../../../FineArt/FineArtRightPanel";
// Inner form components (rendered directly INSIDE the substitution wrapper —
// which is itself a RightPanel — to avoid a drawer-inside-a-drawer). The
// *RightPanel wrappers above are only used for the plain "Add" / edit case.
import { RiskLocation } from "../../../RiskLocation/RiskLocation";
import { Coverage } from "../../../Coverage/Coverage";
import { InterestedParties } from "../../../InterestedParties/InterestedParties";
import { Equipment } from "../../../Equipment/Equipment";
import { ToolFloater } from "../../../ToolFloater/ToolFloater";
import { FineArt } from "../../../FineArt/FineArt";
import { ChangeSectionKey } from "./WhatChangedBoard";
import { RecordReplacePanelV2 } from "./RecordReplacePanelV2";
import { useBusinessPolicyRiskLocation } from "../../../hooks/useBusinessPolicyRiskLocation";
import { useBusinessPolicyCoverage } from "../../../hooks/useBusinessPolicyCoverage";
import { useBusinessIntrestedParty } from "../../../hooks/useBusinessPolicyInterestedParty";
import { useBusinessPolicyEquipment } from "../../../hooks/useBusinessPolicyEquiment";
import { useBusinessPolicyToolFloater } from "../../../hooks/useBusinessPolicyToolFloater";
import { useBusinessPolicyFineArt } from "../../../hooks/useBusinessPolicyFineArt";

// Sections that use a record panel (single-object sections are handled inline).
export type RecordSectionKey =
  | "riskLocationChange"
  | "coverageChange"
  | "interestedPartyChange"
  | "equipmentChange"
  | "toolFloaterChange"
  | "fineArtChange";

export const isRecordSection = (
  section: ChangeSectionKey
): section is RecordSectionKey =>
  [
    "riskLocationChange",
    "coverageChange",
    "interestedPartyChange",
    "equipmentChange",
    "toolFloaterChange",
    "fineArtChange",
  ].includes(section);

export interface IRecordPanelTarget {
  section: RecordSectionKey;
  // undefined => create a new record; number => edit the record at this index.
  editIndex?: number;
  // Optional mode carried over from the board selection (e.g. "Change" for a
  // substitution). Falls back to "Add" when not provided.
  mode?: string | null;
}

// Maps a section to the values.*_change slice key.
const SECTION_TO_SLICE: Record<RecordSectionKey, string> = {
  riskLocationChange: "risk_location_change",
  coverageChange: "coverage_change",
  interestedPartyChange: "interested_party_change",
  equipmentChange: "equipment_change",
  toolFloaterChange: "tool_floater_change",
  fineArtChange: "fine_art_change",
};

interface IRecordPanelHostProps {
  target: IRecordPanelTarget | null;
  values: IBusinessPolicyChange;
  customerId?: number;
  customerPolicyId?: number;
  setValues: (values: any, shouldValidate?: boolean) => any;
  autoSaveData?: (values: IBusinessPolicyChange) => void;
  onClose: () => void;
}

// Shared props each per-section panel needs.
interface ISectionPanelProps {
  target: IRecordPanelTarget;
  values: IBusinessPolicyChange;
  customerId?: number;
  customerPolicyId?: number;
  isEdit: boolean;
  existing: any;
  setValues: (values: any, shouldValidate?: boolean) => any;
  saveRecord: (record: any) => void;
  onClose: () => void;
}

export const RecordPanelHost: React.FC<IRecordPanelHostProps> = ({
  target,
  values,
  customerId,
  customerPolicyId,
  setValues,
  autoSaveData = () => {},
  onClose,
}) => {
  if (!target) {
    return null;
  }

  const sliceKey = SECTION_TO_SLICE[target.section];
  const isEdit = target.editIndex !== undefined;
  const existing =
    isEdit && (values as any)[sliceKey]?.records
      ? (values as any)[sliceKey].records[target.editIndex as number]
      : undefined;

  // Enriches the saved record and writes it back into the right records array.
  // (Faithful port of Automobile V2 saveRecord.)
  const saveRecord = (record: any) => {
    const newValues = produce(values, (draftValues: any) => {
      // Each record carries its OWN mode ("Add" / "Change") and, for a
      // substitution, its OWN snapshot of the record being replaced. This is
      // what keeps a later "Add" from retroactively flipping earlier "Change"
      // records to ADD in the Changes Requested summary — the section-level
      // `type` flag alone can't distinguish per-record modes.
      const recordMode = target.mode === "Change" ? "Change" : "Add";
      const slice = draftValues[sliceKey] || {};
      const replacedSnapshot =
        recordMode === "Change"
          ? (record as any)?.replaced_record_details ??
            slice.delete_risk_location_details ??
            slice.delete_coverage_details ??
            slice.delete_interested_party_details ??
            slice.delete_equipment_details ??
            slice.delete_tool_floater_details ??
            slice.delete_fine_art_details ??
            null
          : undefined;
      const replacedDeleteValue =
        recordMode === "Change"
          ? (record as any)?.replaced_delete_value ??
            slice.delete_risk_location ??
            slice.delete_coverage ??
            slice.delete_interested_party ??
            slice.delete_equipment ??
            slice.delete_tool_floater ??
            slice.delete_fine_art ??
            null
          : null;
      const updatedData = {
        ...record,
        customer_policy_id: customerPolicyId ?? null,
        customer_id: customerId ? Number(customerId) : null,
        // Per-record mode + which existing record this one replaces.
        mode: recordMode,
        replaced_delete_value: replacedDeleteValue,
        replaced_record_details:
          recordMode === "Change" ? replacedSnapshot : null,
      };
      // Defensively initialise the slice if the server returned it as null.
      if (!draftValues[sliceKey]) {
        draftValues[sliceKey] = { type: null, records: [] };
      }
      if (!Array.isArray(draftValues[sliceKey].records)) {
        draftValues[sliceKey].records = [];
      }
      // Default the section mode to "Add" if not already set. When the record
      // panel was opened for a "Change" (substitution) action, keep that mode
      // instead of defaulting to "Add" so it surfaces correctly in "Changes
      // Requested" and is submitted with the right type.
      if (!draftValues[sliceKey].type || target.mode) {
        draftValues[sliceKey].type =
          target.mode || draftValues[sliceKey].type || "Add";
      }
      if (isEdit) {
        draftValues[sliceKey].records[target.editIndex as number] = updatedData;
      } else {
        draftValues[sliceKey].records.push(updatedData);
      }
    });
    autoSaveData(newValues);
    setValues(newValues);
    onClose();
  };

  const sectionProps: ISectionPanelProps = {
    target,
    values,
    customerId,
    customerPolicyId,
    isEdit,
    existing,
    setValues,
    saveRecord,
    onClose,
  };

  // Only the matching section component mounts, so only its list hook (if any)
  // runs. Non-substitution branches inside each component skip the hook data
  // entirely.
  switch (target.section) {
    case "riskLocationChange":
      return <RiskLocationRecordPanel {...sectionProps} />;
    case "coverageChange":
      return <CoverageRecordPanel {...sectionProps} />;
    case "interestedPartyChange":
      return <InterestedPartyRecordPanel {...sectionProps} />;
    case "equipmentChange":
      return <EquipmentRecordPanel {...sectionProps} />;
    case "toolFloaterChange":
      return <ToolFloaterRecordPanel {...sectionProps} />;
    case "fineArtChange":
      return <FineArtRecordPanel {...sectionProps} />;
    default:
      return null;
  }
};

// ---------------------------------------------------------------------------
// Per-section panels. Each is mounted only when its section is the active
// target, so its list hook fires lazily (only when that panel opens). The
// plain "Add"/"Edit" path renders the *RightPanel directly and needs no list.
// ---------------------------------------------------------------------------

const RiskLocationRecordPanel: React.FC<ISectionPanelProps> = ({
  values,
  customerId,
  customerPolicyId,
  isEdit,
  existing,
  target,
  setValues,
  saveRecord,
  onClose,
}) => {
  const isSubstitution = target.mode === "Change";
  // Only fetch the "existing records" list when substituting.
  const { riskLocationsDropDown, riskLocations } = useBusinessPolicyRiskLocation(
    isSubstitution ? customerPolicyId : undefined
  );

  if (isSubstitution) {
    return (
      <RecordReplacePanelV2
        open={true}
        heading="Risk Location Change"
        recordLabel="Risk Location"
        options={riskLocationsDropDown}
        valueToReplace={
          values.risk_location_change?.delete_risk_location ?? null
        }
        disableSelect={isEdit}
        onSelectToReplace={(toReplace) => {
          const matched = riskLocations.find(
            (r: any) => String(r.location_number ?? "") === String(toReplace)
          );
          const newValues = produce(values, (draftValues: any) => {
            if (!draftValues.risk_location_change) {
              draftValues.risk_location_change = { type: null, records: [] };
            }
            draftValues.risk_location_change.type = "Change";
            draftValues.risk_location_change.delete_risk_location = toReplace;
            draftValues.risk_location_change.delete_risk_location_details = {
              location_number:
                matched?.location_number != null
                  ? String(matched.location_number)
                  : String(toReplace),
              address: matched?.address ?? null,
              city: matched?.city ?? null,
              province_or_state: matched?.province_or_state ?? null,
              postal_code: matched?.postal_code ?? null,
            };
          });
          setValues(newValues);
        }}
        onClose={onClose}
      >
        <RiskLocation
          customerId={customerId}
          customerPolicyId={customerPolicyId}
          riskLocationNumber={
            existing ? (existing.location_number as any) : undefined
          }
          onSaveSuccess={saveRecord}
          fromRightPanel
          disableApiCalls
          created_from="Policy_Change"
          initialData={existing}
        />
      </RecordReplacePanelV2>
    );
  }

  return (
    <RiskLocationRightPanel
      open={true}
      customerId={customerId}
      customerPolicyId={customerPolicyId}
      onSaveSuccess={saveRecord}
      created_from="Policy_Change"
      initialData={existing}
      onClose={onClose}
    />
  );
};

const CoverageRecordPanel: React.FC<ISectionPanelProps> = ({
  values,
  customerId,
  customerPolicyId,
  isEdit,
  existing,
  target,
  setValues,
  saveRecord,
  onClose,
}) => {
  const isSubstitution = target.mode === "Change";
  const { coverageDropdown, coverageList } = useBusinessPolicyCoverage(
    isSubstitution ? customerPolicyId : undefined
  );

  if (isSubstitution) {
    return (
      <RecordReplacePanelV2
        open={true}
        heading="Coverage Change"
        recordLabel="Coverage"
        options={coverageDropdown as any}
        valueToReplace={values.coverage_change?.delete_coverage ?? null}
        disableSelect={isEdit}
        onSelectToReplace={(toReplace) => {
          const matched = coverageList.find(
            (c: any) => String(c.coverage_number ?? "") === String(toReplace)
          );
          const newValues = produce(values, (draftValues: any) => {
            if (!draftValues.coverage_change) {
              draftValues.coverage_change = { type: null, records: [] };
            }
            draftValues.coverage_change.type = "Change";
            draftValues.coverage_change.delete_coverage = toReplace;
            draftValues.coverage_change.delete_coverage_details = {
              coverage_number:
                matched?.coverage_number != null
                  ? String(matched.coverage_number)
                  : String(toReplace),
              coverage_type: (matched as any)?.coverage_type ?? null,
            };
          });
          setValues(newValues);
        }}
        onClose={onClose}
      >
        <Coverage
          customerId={customerId}
          customerPolicyId={customerPolicyId}
          onSaveSuccess={saveRecord}
          fromRightPanel
          disableApiCalls
          created_from="Policy_Change"
          initialData={existing}
        />
      </RecordReplacePanelV2>
    );
  }

  return (
    <CoverageRightPanel
      open={true}
      customerId={customerId}
      customerPolicyId={customerPolicyId}
      onSaveSuccess={saveRecord}
      created_from="Policy_Change"
      initialData={existing}
      onClose={onClose}
    />
  );
};

const InterestedPartyRecordPanel: React.FC<ISectionPanelProps> = ({
  values,
  customerId,
  customerPolicyId,
  isEdit,
  existing,
  target,
  setValues,
  saveRecord,
  onClose,
}) => {
  const isSubstitution = target.mode === "Change";
  const { interestedPartyDropDown, interestedPartyList } =
    useBusinessIntrestedParty(isSubstitution ? customerPolicyId : undefined);

  if (isSubstitution) {
    return (
      <RecordReplacePanelV2
        open={true}
        heading="Interested Party Change"
        recordLabel="Interested Party"
        options={interestedPartyDropDown as any}
        valueToReplace={
          values.interested_party_change?.delete_interested_party ?? null
        }
        disableSelect={isEdit}
        onSelectToReplace={(toReplace) => {
          const matched = interestedPartyList.find(
            (ip: any) =>
              String(ip.incremental_number ?? "") === String(toReplace)
          );
          const newValues = produce(values, (draftValues: any) => {
            if (!draftValues.interested_party_change) {
              draftValues.interested_party_change = {
                type: null,
                records: [],
              };
            }
            draftValues.interested_party_change.type = "Change";
            draftValues.interested_party_change.delete_interested_party =
              toReplace;
            draftValues.interested_party_change.delete_interested_party_details =
              {
                incremental_number:
                  matched?.incremental_number != null
                    ? String(matched.incremental_number)
                    : String(toReplace),
                interested_party_name: matched?.interested_party_name ?? null,
                interested_party_code: matched?.interested_party_code ?? null,
              };
          });
          setValues(newValues);
        }}
        onClose={onClose}
      >
        <InterestedParties
          customerId={customerId}
          customerPolicyId={customerPolicyId}
          interestedNumber={
            existing ? (existing.incremental_number as any) : undefined
          }
          onSaveSuccess={saveRecord}
          fromRightPanel
          disableApiCalls
          created_from="Policy_Change"
          initialData={existing}
        />
      </RecordReplacePanelV2>
    );
  }

  return (
    <InterestedPartiesRightPanel
      open={true}
      customerId={customerId}
      customerPolicyId={customerPolicyId}
      interestedNumber={
        existing ? (existing.incremental_number as any) : undefined
      }
      onSaveSuccess={saveRecord}
      createdFrom="Policy_Change"
      initialData={existing}
      onClose={onClose}
    />
  );
};

const EquipmentRecordPanel: React.FC<ISectionPanelProps> = ({
  values,
  customerId,
  customerPolicyId,
  isEdit,
  existing,
  target,
  setValues,
  saveRecord,
  onClose,
}) => {
  const isSubstitution = target.mode === "Change";
  const { equipmentDropdown } = useBusinessPolicyEquipment(
    isSubstitution ? customerPolicyId : undefined
  );

  if (isSubstitution) {
    return (
      <RecordReplacePanelV2
        open={true}
        heading="Equipment Change"
        recordLabel="Equipment"
        options={equipmentDropdown as any}
        valueToReplace={values.equipment_change?.delete_equipment ?? null}
        disableSelect={isEdit}
        onSelectToReplace={(toReplace) => {
          const newValues = produce(values, (draftValues: any) => {
            if (!draftValues.equipment_change) {
              draftValues.equipment_change = { type: null, records: [] };
            }
            draftValues.equipment_change.type = "Change";
            draftValues.equipment_change.delete_equipment = toReplace;
            draftValues.equipment_change.delete_equipment_details = {
              incremental_number: String(toReplace),
            };
          });
          setValues(newValues);
        }}
        onClose={onClose}
      >
        <Equipment
          customerId={customerId}
          customerPolicyId={customerPolicyId}
          onSaveSuccess={saveRecord}
          fromRightPanel
          disableApiCalls
          created_from="Policy_Change"
          initialData={existing}
        />
      </RecordReplacePanelV2>
    );
  }

  return (
    <EquipmentRightPanel
      open={true}
      customerId={customerId}
      customerPolicyId={customerPolicyId}
      onSaveSuccess={saveRecord}
      created_from="Policy_Change"
      initialData={existing}
      onClose={onClose}
    />
  );
};

const ToolFloaterRecordPanel: React.FC<ISectionPanelProps> = ({
  values,
  customerId,
  customerPolicyId,
  isEdit,
  existing,
  target,
  setValues,
  saveRecord,
  onClose,
}) => {
  const isSubstitution = target.mode === "Change";
  const { toolFloaterDropdown } = useBusinessPolicyToolFloater(
    isSubstitution ? customerPolicyId : undefined
  );

  if (isSubstitution) {
    return (
      <RecordReplacePanelV2
        open={true}
        heading="Tool Floater Change"
        recordLabel="Tool Floater"
        options={toolFloaterDropdown as any}
        valueToReplace={
          values.tool_floater_change?.delete_tool_floater ?? null
        }
        disableSelect={isEdit}
        onSelectToReplace={(toReplace) => {
          const newValues = produce(values, (draftValues: any) => {
            if (!draftValues.tool_floater_change) {
              draftValues.tool_floater_change = { type: null, records: [] };
            }
            draftValues.tool_floater_change.type = "Change";
            draftValues.tool_floater_change.delete_tool_floater = toReplace;
            draftValues.tool_floater_change.delete_tool_floater_details = {
              incremental_number: String(toReplace),
            };
          });
          setValues(newValues);
        }}
        onClose={onClose}
      >
        <ToolFloater
          customerId={customerId}
          customerPolicyId={customerPolicyId}
          onSaveSuccess={saveRecord}
          fromRightPanel
          disableApiCalls
          created_from="Policy_Change"
          initialData={existing}
        />
      </RecordReplacePanelV2>
    );
  }

  return (
    <ToolFloaterRightPanel
      open={true}
      customerId={customerId}
      customerPolicyId={customerPolicyId}
      onSaveSuccess={saveRecord}
      created_from="Policy_Change"
      initialData={existing}
      onClose={onClose}
    />
  );
};

const FineArtRecordPanel: React.FC<ISectionPanelProps> = ({
  values,
  customerId,
  customerPolicyId,
  isEdit,
  existing,
  target,
  setValues,
  saveRecord,
  onClose,
}) => {
  const isSubstitution = target.mode === "Change";
  const { fineArtDropdown } = useBusinessPolicyFineArt(
    isSubstitution ? customerPolicyId : undefined
  );

  if (isSubstitution) {
    return (
      <RecordReplacePanelV2
        open={true}
        heading="Fine Art Change"
        recordLabel="Fine Art"
        options={fineArtDropdown as any}
        valueToReplace={values.fine_art_change?.delete_fine_art ?? null}
        disableSelect={isEdit}
        onSelectToReplace={(toReplace) => {
          const newValues = produce(values, (draftValues: any) => {
            if (!draftValues.fine_art_change) {
              draftValues.fine_art_change = { type: null, records: [] };
            }
            draftValues.fine_art_change.type = "Change";
            draftValues.fine_art_change.delete_fine_art = toReplace;
            draftValues.fine_art_change.delete_fine_art_details = {
              incremental_number: String(toReplace),
            };
          });
          setValues(newValues);
        }}
        onClose={onClose}
      >
        <FineArt
          customerId={customerId}
          customerPolicyId={customerPolicyId}
          onSaveSuccess={saveRecord}
          fromRightPanel
          disableApiCalls
          created_from="Policy_Change"
          initialData={existing}
        />
      </RecordReplacePanelV2>
    );
  }

  return (
    <FineArtRightPanel
      open={true}
      customerId={customerId}
      customerPolicyId={customerPolicyId}
      onSaveSuccess={saveRecord}
      created_from="Policy_Change"
      initialData={existing}
      onClose={onClose}
    />
  );
};
